// @ts-strict-ignore
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { forkJoin, lastValueFrom, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { ProjectModel } from '@project/model/project.model';
import { ProjectHelper } from '@project/helper/project.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { BorderDataVO, LabelTextureDataVO, LabelTextureDescription } from './texture.vo';
import { ContainerService } from '../model3d/container.service';
import { ArticleContainer } from '../model3d/article-container';
import { CompressionSimpleTextValueVO, MetaElementUsageIdentityVO, MetaElementUsageVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { Connection } from '../model3d/connection';
import { TextureConnectionInfo } from '../model3d/texture-connection-info';
import { TextureAreaBorderParameters } from '../model/texture-area-border-parameters';
import { TextureUtils } from '../model3d/util/texture-utils';
import { TextureCacheManager } from '@project/components/article/manager/texture-cache.manager';
import { Log } from '@shared/debug/log';
import { SceneManager } from '../manager/scene.manager';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '../model3d/util/connection-info-utils';
import { ConnectionInfoService } from '../model3d/service/connection-info.service';
import { ArticleUtils } from '@article/util/article-utils';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { ConstraintCheckUtils } from '@project/components/label/label-editor/constraint-check-validation/constraint-check-utils';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';

@Injectable({ providedIn: 'root' })
export class TextTextureProcessor {
  private static readonly LABEL_TEXTURE_NAME: string = 'labelTexture';
  private static readonly LABEL_TEXTURE_TYPE: string = 'png';
  private static readonly COLOR_BLACK: number = 0x000000;
  private static readonly COLOR_GRAY: number = 0xcdcdcd;

  constructor(
    private readonly textureCacheManager: TextureCacheManager,
    private readonly projectModel: ProjectModel,
    private readonly projectHelper: ProjectHelper,
    private readonly sceneManager: SceneManager,
    private readonly containerService: ContainerService,
    private readonly connectionInfoService: ConnectionInfoService,
    private readonly articleContainerUtils: ArticleContainerUtils,
    private readonly textSizeCheckManager: TextSizeCheckManager,
    private readonly labelingUtils: LabelingUtils,
  ) {}

  public async loadAndApplyTexturesToArticleContainers(articleContainers: ArticleContainer[]): Promise<void> {
    if (ArrayUtils.isEmpty(articleContainers) || !this.projectModel.texturesEnabled) {
      return;
    }
    try {
      await this.createLabelTextureRequestForArticleContainers(articleContainers);
    } finally {
      this.sceneManager.renderScene();
    }
  }

  public async loadAndApplyTexturesToConnections(connections: Connection[]): Promise<LabelTextureDataVO[]> {
    if (ArrayUtils.isEmpty(connections) || !this.projectModel.texturesEnabled) {
      return [];
    }
    await this.adjustCompressionForTextValues(connections);
    const labelTextureDescriptions: LabelTextureDescription[] = [];
    for (const connection of connections) {
      const labelTextureDescription: LabelTextureDescription = this.createLabelTextureData(connection);
      if (labelTextureDescription) {
        labelTextureDescriptions.push(labelTextureDescription);
      }
    }
    return this.createLabelTextureRequest(labelTextureDescriptions);
  }

  private createLabelTextureRequestForArticleContainers(articleContainers: ArticleContainer[]): Promise<LabelTextureDataVO[]> {
    const connections = articleContainers.flatMap((articleContainer) => articleContainer.connections);
    return this.loadAndApplyTexturesToConnections(connections);
  }

  private async createLabelTextureRequest(labelTextureDescriptions: LabelTextureDescription[]): Promise<LabelTextureDataVO[]> {
    const labelTextureGroups: LabelTextureDescription[][] = this.groupLabelTextureDescriptions(labelTextureDescriptions);
    if (ArrayUtils.isEmpty(labelTextureGroups)) {
      return [];
    }
    return lastValueFrom(this.textureCacheManager
      .getLabelTextures(labelTextureGroups)
      .pipe(mergeMap((labelTextures: LabelTextureDataVO[]) => this.applyImageTextures(labelTextures))));
  }

  private groupLabelTextureDescriptions(labelTextureDescriptions: LabelTextureDescription[]): LabelTextureDescription[][] {
    if (ArrayUtils.isEmpty(labelTextureDescriptions)) {
      return [];
    }
    const labelTextureGroups: LabelTextureDescription[][] = [];
    const textureDescriptions: LabelTextureDescription[] = labelTextureDescriptions.concat();
    while (textureDescriptions.length > 0) {
      const labelTextureGroup: LabelTextureDescription[] = this.createGroup(textureDescriptions);
      if (labelTextureGroup) {
        labelTextureGroups.push(labelTextureGroup);
      } else {
        // can't find visible/parent connection (without link to previous previousMergedUsageId) in the given textureDescriptions
        // so while-loop can be stopped
        Log.info('Error: TextTextureProcessor: error occurred during generation of the label requests!');
        break;
      }
    }
    return labelTextureGroups;
  }

  private createGroup(textureDescriptions: LabelTextureDescription[]): LabelTextureDescription[] {
    let labelTextureDescription: LabelTextureDescription;
    for (const textureDescription of textureDescriptions) {
      if (!textureDescription.textureAreaUsageVO.previousMergedUsageId) {
        labelTextureDescription = textureDescription;
        break;
      }
    }
    if (!labelTextureDescription) {
      return null;
    }
    const group: LabelTextureDescription[] = [];
    group.push(labelTextureDescription);
    textureDescriptions.splice(textureDescriptions.indexOf(labelTextureDescription), 1);
    while (labelTextureDescription.textureAreaUsageVO.nextMergedUsageId) {
      const connectionId: MetaElementUsageIdentityVO = labelTextureDescription.textureAreaUsageVO.nextMergedUsageId;
      labelTextureDescription = this.getNextLabelTextureDescription(connectionId, textureDescriptions);
      if (!labelTextureDescription) {
        return null;
      }
      group.push(labelTextureDescription);
    }
    return group;
  }

  private getNextLabelTextureDescription(
    connectionId: MetaElementUsageIdentityVO,
    textureDescriptions: LabelTextureDescription[]
  ): LabelTextureDescription {
    let labelTextureDescription: LabelTextureDescription;
    for (const textureDescription of textureDescriptions) {
      if (textureDescription.textureAreaUsageVO.identity.equals(connectionId)) {
        labelTextureDescription = textureDescription;
        break;
      }
    }
    if (labelTextureDescription) {
      textureDescriptions.splice(textureDescriptions.indexOf(labelTextureDescription), 1);
    }
    return labelTextureDescription;
  }

  private createLabelTextureData(connection: Connection): LabelTextureDescription {
    if (
      !connection ||
      !connection.metaElementUsage ||
      !connection.articleContainer ||
      !(connection.metaElementUsage instanceof TextureAreaUsageVO) ||
      (connection.type === Connection.TYPE_IMAGE && ConnectionInfoUtils.hasDynamicPinReference(connection.metaElementVO))
    ) {
      return null;
    }
    const articleContainer: ArticleContainer = connection.articleContainer;
    const labelTextureDescription: LabelTextureDescription = new LabelTextureDescription();
    labelTextureDescription.height = connection.compositionRectangle.height;

    const isMcsGdsLtlArticle = this.articleContainerUtils.isOrIsPartOfMcsGdsLtl(connection.articleContainer);

    const isDirectPrintOnMcsGdsLtlArticle= !ArticleUtils.isLabelingArticle(connection.articleContainer.article) && isMcsGdsLtlArticle;

    labelTextureDescription.keepAspect = isDirectPrintOnMcsGdsLtlArticle;
    labelTextureDescription.borderData = this.createBorderData(connection);
    const textureAreaUsage: MetaElementUsageVO = connection.metaElementUsage;
    if (textureAreaUsage instanceof TextureAreaUsageVO) {
      labelTextureDescription.textureAreaUsageVO = textureAreaUsage;
    }
    const textureConnectionInfo: TextureConnectionInfo = articleContainer.findTextureConnectionInfo(connection.id);
    if (textureConnectionInfo) {
      labelTextureDescription.width = textureConnectionInfo.connectionWidth;
      labelTextureDescription.distanceToPreviousConnection = textureConnectionInfo.distanceToPreviousConnection;
    } else {
      labelTextureDescription.width = connection.localRectangle.width;
      labelTextureDescription.distanceToPreviousConnection = 0;
    }

    return labelTextureDescription;
  }

  private createBorderData(connection: Connection): BorderDataVO[] {
    const borderData: BorderDataVO[] = [];
    let order: number = 0;
    const textureAreaBorderParameters: TextureAreaBorderParameters = this.projectHelper.createTextureAreaBorder(connection);
    if (textureAreaBorderParameters) {
      if (textureAreaBorderParameters.forceDrawBorder || this.isTextureBorderRequired(connection)) {
        // If textureAreaBorderParameters are not null then full '[]' border must be drawn.
        if (textureAreaBorderParameters.drawOuterBorder) {
          // Additional outer border must be drawn first
          const outerBorderData: BorderDataVO = this.createBorderDataVO(order, TextTextureProcessor.COLOR_GRAY, true);
          order++;
          borderData.push(outerBorderData);
        }
        const squareBorderData: BorderDataVO = this.createBorderDataVO(order, textureAreaBorderParameters.borderColor, true);
        order++;
        borderData.push(squareBorderData);
      }
    } else {
      const drawLines: boolean = this.connectionInfoService.isDrawingLinesAvailable(connection);
      if (drawLines) {
        // Draw lines at the left and right of the texture '| |'
        const leftRightBorderData: BorderDataVO = this.createBorderDataVO(order, TextTextureProcessor.COLOR_BLACK, false);
        order++;
        borderData.push(leftRightBorderData);
      }
    }
    return borderData;
  }

  private isTextureBorderRequired(connection: Connection): boolean {
    if (Connection.TYPE_TEXT_OR_IMAGE_USAGE === connection.type && connection.isImageLoaded) {
      return false;
    }
    return !TextureUtils.hasConnectionTextureText(connection);
  }

  private createBorderDataVO(order: number, color: number, isFullBorder: boolean): BorderDataVO {
    const borderData: BorderDataVO = new BorderDataVO();
    borderData.order = order;
    borderData.color = color;
    borderData.isFullBorder = isFullBorder;
    return borderData;
  }

  private applyImageTextures(labelTextureDataArray: LabelTextureDataVO[]): Observable<any> {
    if (ArrayUtils.isEmpty(labelTextureDataArray)) {
      return of(null);
    }
    const materials: Observable<any>[] = new Array<Observable<any>>();
    for (const labelTextureData of labelTextureDataArray) {
      if (!labelTextureData.textureData) {
        materials.push(of(null));
        continue;
      }
      const identity: MetaElementUsageIdentityVO = labelTextureData.identity;
      const materialData: Observable<any> = TextureUtils.createImage(
        labelTextureData.textureData,
        TextTextureProcessor.LABEL_TEXTURE_TYPE,
        TextTextureProcessor.LABEL_TEXTURE_NAME
      ).pipe(
        mergeMap((image: HTMLImageElement) => {
          const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(identity.articleUsageId);
          if (articleContainer && image) {
            const connection: Connection = articleContainer.findConnectionByName(identity.metaElementName);
            if (connection) {
              return this.mergeBitmaps(connection, image).pipe(
                map(() => {
                  connection.isTextureLoaded = true;
                  connection.refreshTexture();
                })
              );
            }
          }
          return of(null);
        })
      );
      materials.push(materialData);
    }
    return forkJoin(materials);
  }

  private mergeBitmaps(connection: Connection, image: HTMLImageElement): Observable<HTMLImageElement> {
    if (connection.imageBitmap) {
      return TextureUtils.mergeImages(connection.imageBitmap, image, false).pipe(
        map((mergedImage: HTMLImageElement) => {
          connection.backgroundBitmap = mergedImage;
          connection.imageBitmap = image;
          return mergedImage;
        })
      );
    } else {
      connection.imageBitmap = image;
      return of(image);
    }
  }

  private async adjustCompressionForTextValues(connections: Connection[]): Promise<void> {
    await this.textSizeCheckManager.retrieveTextSizeCheckInfos(this.textSizeCheckManager.buildCommonTextSizeCheckRequest(connections));
    for (const connection of connections) {
      if (connection.metaElementUsage instanceof TextureAreaUsageVO
        && connection.metaElementUsage.textValue instanceof CompressionSimpleTextValueVO
        && connection.metaElementUsage.textValue.autoCompression
      ) {
        const spacingData = this.labelingUtils.getTextCheckSpacingDataByTextureCube(connection);
        const textCheckClass = PropertyUtils.getStringPropertyValue(connection.metaElementVO.behaviorProperties, MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS);
        const textSizeChecks = this.textSizeCheckManager.getTextSizeCheckWithFallback(
          textCheckClass,
          spacingData,
          ArticleUtils.isDynamicLabelingArticle(connection.article),
        );
        connection.metaElementUsage.textValue.compression = ConstraintCheckUtils.calculateCompressionForTextValue(
          connection.metaElementUsage.textValue,
          textSizeChecks,
          textCheckClass,
        );
      }
    }
  }
}
